/**
 * Endpoints de Auth — todos contra el backend real (everex-traza-back).
 *
 *   - login            → POST /api/auth/login   (usuario + password)
 *   - loginConGoogle   → POST /api/auth/google  (idToken de Google Identity Services)
 *   - reautenticar     → POST /api/auth/reauth  (step-up auth, requiere Bearer)
 *   - logout           → POST /api/auth/logout  (requiere Bearer)
 *
 * `apiFetch` se encarga de adjuntar el Bearer, normalizar errores a `ApiError`
 * y disparar el auto-logout si el backend responde 401 AUTH_TOKEN_EXPIRED.
 */

import {
  LoginRequest,
  LoginResponse,
  GoogleLoginRequest,
  ReauthRequest,
  ReauthResponse,
} from '@/api/contracts/auth';
import { apiFetch, setToken, clearToken, ApiError } from '@/api/http';

// Re-export para no romper imports existentes (`@/api/endpoints` → ApiError).
export { ApiError };

/**
 * POST /api/auth/login. Guarda el JWT devuelto y retorna { usuario, loginAt }
 * (token/expiresAt se descartan del shape público — zod hace strip).
 */
export async function login(input: LoginRequest): Promise<LoginResponse> {
  const parsed = LoginRequest.parse(input);
  const data = await apiFetch<{ token?: string }>('/auth/login', {
    method: 'POST',
    body: parsed,
  });
  if (data?.token) setToken(data.token);
  return LoginResponse.parse(data);
}

/**
 * POST /api/auth/google — login con Google Workspace SSO (paquete AB).
 * El backend verifica el idToken con google-auth-library, valida `hd` contra
 * GOOGLE_ALLOWED_DOMAINS, busca el user por email (sin auto-provision) y
 * devuelve nuestro propio JWT (NO el idToken de Google).
 */
export async function loginConGoogle(input: GoogleLoginRequest): Promise<LoginResponse> {
  const parsed = GoogleLoginRequest.parse(input);
  const data = await apiFetch<{ token?: string }>('/auth/google', {
    method: 'POST',
    body: parsed,
  });
  if (data?.token) setToken(data.token);
  return LoginResponse.parse(data);
}

/** POST /api/auth/reauth — step-up auth para acciones sensibles. */
export async function reautenticar(input: ReauthRequest): Promise<ReauthResponse> {
  const parsed = ReauthRequest.parse(input);
  const data = await apiFetch<ReauthResponse>('/auth/reauth', {
    method: 'POST',
    body: parsed,
    auth: true,
  });
  return ReauthResponse.parse(data);
}

/**
 * POST /api/auth/refresh — sliding session. Re-emite el JWT y actualiza el
 * token local. Se llama periódicamente mientras hay sesión + actividad, para
 * que el token no expire mientras el usuario opera.
 */
export async function refreshToken(): Promise<void> {
  const data = await apiFetch<{ token?: string }>('/auth/refresh', {
    method: 'POST',
    auth: true,
  });
  if (data?.token) setToken(data.token);
}

/** POST /api/auth/logout — best-effort server-side + limpia token local. */
export async function logout(): Promise<void> {
  try {
    await apiFetch('/auth/logout', { method: 'POST', auth: true });
  } catch {
    // No bloquear el logout local si el server falla (token expirado, red caída, etc.)
  } finally {
    clearToken();
  }
}
